import AuthCourseSupport from "../../components/Auth/AuthCourseSupport";
import { coursesEnabled } from "../../config/courseFeatures";
import React, { useEffect, useRef, useState } from "react";
import { FaHouse } from "react-icons/fa6";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/Sidebar/Dark_logo.png";
import facebookIcon from "../../assets/Icons/facebook.svg";
import googleIcon from "../../assets/Icons/google.svg";
import appleIcon from "../../assets/Icons/apple.svg";
import blueBg from "../../assets/Login/bluebg.svg";
import avatar from "../../assets/Icons/user-avatar.png";
import edit from "../../assets/Icons/fluent_edit.svg";
import { useThunk } from "../../hooks/useThunk";
import useForm from "../../hooks/useForm";
import { signup } from "../../store";
import showToast from "../../utils/toastNotifications";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import LoginInput from "../../components/Form/LoginInput";
import LoginButton from "../../components/Form/LoginButton";
import { getGoogleAuthUrl } from "../../utils/googleAuth";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { clearStoredReferralCode, getStoredReferralCode, storeReferralCode } from "../../utils/referralTracking";

const SignUp = () => {
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      firstName: "",
      lastName: "",
      email: "",
      // phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    {
      firstName: true,
      lastName: true,
      email: true,
      // phoneNumber: true,
      password: true,
      confirmPassword: true,
    },
    {
      firstName: USER_INPUT_FIELD_TYPES.FIRST_NAME,
      lastName: USER_INPUT_FIELD_TYPES.LAST_NAME,
      email: USER_INPUT_FIELD_TYPES.EMAIL,
      // phoneNumber: USER_INPUT_FIELD_TYPES.PHONE,
      password: USER_INPUT_FIELD_TYPES.PASSWORD,
      confirmPassword: USER_INPUT_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(true);
  const [doSignup, isSigningUp] = useThunk(signup);
  const [uploadedImage, setUploadedImage] = useState(null); // State for uploaded image
  const [doUpload, isUploadingImage] = useFirebaseFileUpload();
  const fileInputRef = useRef(null); // Ref for the hidden file input
  const [activeReferralCode, setActiveReferralCode] = useState("");
  const [inlineMessage, setInlineMessage] = useState("");

  const duplicateEmailMessage =
    "There is already another user with the same email address. Please log in instead or use another email.";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const referralCode = params.get("ref");

    if (referralCode) {
      storeReferralCode(referralCode).then(() => {
        setActiveReferralCode(getStoredReferralCode());
      });
      return;
    }

    setActiveReferralCode(getStoredReferralCode());
  }, []);

  useEffect(() => {
    if (!inlineMessage) return;

    const timeoutId = setTimeout(() => {
      setInlineMessage("");
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [inlineMessage]);

  const toSentenceCase = (message = "") => {
    const trimmedMessage = `${message || ""}`.trim().toLowerCase();
    if (!trimmedMessage) return "";
    return trimmedMessage.replace(/(^\s*\w|[.!?]\s*\w)/g, (match) => match.toUpperCase());
  };

  const getSignupErrorMessage = (message = "") => {
    const normalizedMessage = `${message || ""}`.toLowerCase();

    if (
      normalizedMessage.includes("already") ||
      normalizedMessage.includes("exist") ||
      normalizedMessage.includes("duplicate")
    ) {
      return duplicateEmailMessage;
    }

    return toSentenceCase(message) || "Unable to create account. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInlineMessage("");

    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      // phoneNumber: true,
      password: true,
      confirmPassword: true,
    });

    if (!isTermsAccepted) {
      showToast("caution", "You must accept the terms and conditions.");
      return;
    }

    if (validateForm()) {
      let updatedFormData = { ...formData };
      const storedReferralCode = getStoredReferralCode();

      if (storedReferralCode) {
        updatedFormData.referralCode = storedReferralCode;
      }

      //upload image if provide
      if (uploadedImage) {
        const uploadResult = await doUpload(
          [uploadedImage],
          "user/profilePicture"
        );
        if (!uploadResult.success) {
          showToast("error", uploadResult?.error?.message || "Failed to upload files");
          return;
        }
        updatedFormData.profilePicture = uploadResult?.uploadedUrls[0];
      }

      const result = await doSignup(updatedFormData);

      if (result.success) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          // phoneNumber: "",
          password: "",
          confirmPassword: "",
        });
        localStorage.setItem("token", result.response.data.token);
        clearStoredReferralCode();
        showToast("success", "Sign up successful.");
        navigate("/"); // navigate to dashbord
      } else {
        const errorMessage = getSignupErrorMessage(result.error?.message);
        setInlineMessage(errorMessage);
        showToast("error", errorMessage);
      }
    }
  };

  // redirect user to google concent screen
  const handleGoogleSignin = (e) => {
    e.preventDefault();
    const authUrl = getGoogleAuthUrl();
    if (!authUrl) {
      showToast("error", errorMessages.FAILED_GOOGLE_SIGNIN);
      return;
    }
    window.location.href = authUrl;
  };

  const handleEditImageClick = () => {
    fileInputRef.current.click(); // Trigger click on the hidden file input
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedImage(file);
    }
  };

  return (
    <div className="auth-page-shell auth-page-signup flex flex-col min-w-0 relative w-full overflow-x-hidden">
      {/* WatermarkBg*/}


      <div className="absolute z-0 top-0 right-0 w-full login-br:block">
        <img
          src={blueBg}
          alt="background"
          className="hidden lg:block absolute -right-15 -top-20 lg:right-0 w-full  lg:max-w-[650px] xl:max-w-[700px] 2xl:max-w-[750px] 1920-br:max-w-[850px] 2560-br:max-w-[1400px] transition-all duration-300"
        />
      </div>

      {/* Form Container-left  */}
      <div className="auth-content-shell relative z-10 mx-auto flex flex-col lg:flex-row flex-grow items-start justify-center login-br:justify-between w-full 2xl:max-w-[1536px] auth-layout-structure-in-tab-screen-higher-than-880-height">
        <div className="auth-panel-primary w-full lg:w-[60%] xl:w-[55%] 2xl:w-[50%] flex flex-col justify-center px-4 lg:px-4 ml-0 bg-transparent rounded-[32px] 1920-br:translate-x-[80px]">
          <div className="auth-card w-full bg-white rounded-[32px] p-6 lg:p-8 border border-solid border-[color:lightskyblue] shadow-none lg:shadow-[0_20px_35px_rgba(15,23,42,0.06)]">
            <div className="auth-card-header flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col sm:flex-row-reverse sm:justify-end">
                <img
                  src={logo}
                  alt="CoursePivot"
                  className="sm:ml-[12px] mb-5 sm:mb-0 px-[8px] h-[40px] w-[150px] sm:w-auto object-contain"
                />
                <h1 className="text-level-1 font-roboto font-bold leading-[44px] text-primary mb-2 sm:mb-4">
                  Create Account
                </h1>
              </div>
              <a
                href="https://coursepivot.com"
                className="inline-flex items-center gap-2 self-start rounded-lg border border-medium-gray px-3 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary hover:bg-soft-blue"
              >
                <FaHouse className="h-4 w-4" />
                Home
              </a>
            </div>
          <p className="auth-intro mb-0 sm:mb-10 font-inter text-[18px] text-gray w-full max-w-none sm:max-w-[200px] md:max-w-[220px] lg:max-w-[260px] xl:max-w-none">
            Register for free to create and manage your orders securely.
          </p>
          {activeReferralCode && (
            <p className="mb-4 mt-3 inline-flex rounded-lg bg-soft-blue px-3 py-2 font-inter text-sm font-semibold text-primary">
              Referral code active
            </p>
          )}

          <form
            onSubmit={handleSubmit}
            className="auth-form w-full max-w-full max-sm:mt-4"
          >
            <div className="auth-fields auth-signup-fields">
              {/* Profile Picture */}
              {/* profile picture upload feature removed as per client requirement */}
              {/* {uploadedImage ? (
                <div className="relative">
                  <div
                    className="rounded-full w-8 h-8 bg-primary absolute left-[102px] top-[97px] items-center justify-center"
                    onClick={handleEditImageClick}
                  >
                    <img
                      src={edit}
                      alt="imgEdit"
                      className="items-center justify-center p-[6px] cursor-pointer"
                    />
                  </div>
                  <img
                    src={URL.createObjectURL(uploadedImage)}
                    alt="Uploaded"
                    className="w-[120px] h-[120px] object-cover rounded-[5px] mt-3 border border-secondary"
                  />
                </div>
              ) : (
                <div>
                  <div className="border border-secondary w-[120px] h-[120px] rounded-[5px] mt-3">
                    <img
                      src={avatar}
                      alt="useravatar"
                      onClick={handleEditImageClick}
                      className=" cursor-pointer p-[6px] rounded-[5px] w-[120px] h-[120px] items-center justify-center"
                    />
                    <img
                      src={edit}
                      alt="imgEdit"
                      onClick={handleEditImageClick}
                      className=" cursor-pointer p-[6px] rounded-full w-auto h-8 bg-primary relative left-[100px] top-[-20px] items-center justify-center"
                    />
                  </div>
                </div>
              )} */}

              {/* Hidden File Input */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="hidden"
                onChange={handleImageChange}
              />

              {/* First Name and Last Name */}
              <div className="auth-name-fields grid grid-cols-2 1440-br:grid-cols-2 gap-3 1440-br:gap-4 sm:mr-10 lg:mr-0 mt-4">
                <LoginInput
                  className="px-4"
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  placeholder="First name"
                  error={touched.firstName && errors.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  onBlur={() => handleBlur("firstName", formData.firstName)}
                />
                <LoginInput
                  className="px-4"
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  placeholder="Last name"
                  error={touched.lastName && errors.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  onBlur={() => handleBlur("lastName", formData.lastName)}
                />
              </div>

              {/* Email and Phone */}
              <div className="auth-email-field grid grid-cols-1 md:gap-1 1440-br:gap-6 sm:mr-10 lg:mr-0">
                <LoginInput
                  className="px-4"
                  id="email"
                  name="email"
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  placeholder="Email address"
                  error={touched.email && errors.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email", formData.email)}
                />
              </div>

              {/* Password and Confirm Password */}
              <div className="auth-password-field grid grid-cols-1 1440-br:grid-cols-1 md:gap-1 1440-br:gap-6 sm:mr-10 lg:mr-0">
                <LoginInput
                  className="px-4"
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  placeholder="Password"
                  error={touched.password && errors.password}
                  showPasswordToggle
                  isPasswordVisible={showPassword}
                  togglePasswordVisibility={() =>
                    setShowPassword(!showPassword)
                  }
                  onChange={(e) =>{
                    handleInputChange("password", e.target.value)
                    handleInputChange("confirmPassword", e.target.value) 
                  }
                  }
                  onBlur={() => handleBlur("password", formData.password)}
                />
                {/* confirm password is removed as per the client requirement */}
                {/* <LoginInput
                  className="px-4"
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  placeholder="Confirm your password"
                  error={touched.confirmPassword && errors.confirmPassword}
                  showPasswordToggle
                  isPasswordVisible={showConfirmPassword}
                  togglePasswordVisibility={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  onBlur={() =>
                    handleBlur("confirmPassword", formData.confirmPassword)
                  }
                /> */}
              </div>
            </div>

            <div className="auth-actions flex flex-col items-center justify-center sm:mr-10 lg:mr-0">
              {inlineMessage && (
                <p className="mb-2 w-full max-w-[460px] px-2 text-center font-inter text-[13px] font-medium leading-5 text-light-red">
                  {inlineMessage}
                </p>
              )}

              {/* Sign-Up Button */}
              <LoginButton
                text={
                  isSigningUp || isUploadingImage ? (
                    <ClipLoader
                      loading={isSigningUp || isUploadingImage}
                      speedMultiplier={1}
                      size={22}
                      color="#60a5fa"
                    />
                  ) : (
                    "Sign Up"
                  )
                }
                type="submit"
                className="w-full max-w-[460px] py-3 h-[52px]"
                onClick={handleSubmit}
                disabled={isSigningUp || isUploadingImage}
              />

              {/* Sign-In Link */}
              <p className="mt-3 font-lato text-[15px] text-center leading-[18.75px] text-black/50">
                Already have an account?{" "}
                <a href="/signin" className="text-blue-shades hover:underline">
                  Sign In
                </a>
              </p>

              {/* social logins */}
              <div className="auth-social flex mt-6 w-full max-w-[480px]">
                <div className="w-full max-w-[480px]">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-medium-gray"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 font-inter font-medium text-level-3 leading-[24px] text-gray bg-white">
                        or
                      </span>
                    </div>
                  </div>

                  <div className="auth-google-row grid grid-cols-1 h-[54px] gap-3 mt-3">
                    {/* <button
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      disabled={isSigningUp || isUploadingImage}
                      type="button"
                    >
                      <img
                        src={facebookIcon}
                        alt="Facebook"
                        className="w-5 h-5"
                      />
                    </button> */}
                   <button
                      className="flex items-center justify-center gap-3 font-inter font-semibold text-[18px] leading-[21.6px] text-gray py-3 rounded-lg hover:bg-[#EFEFEF] transition duration-200 px-4 py-2 border rounded-md border-medium-gray "
                      onClick={handleGoogleSignin}
                      disabled={isSigningUp || isUploadingImage}
                    >
                      <img src={googleIcon} alt="Google" className="w-5 h-5" />
                      <span>Continue with Google</span>
                    </button>
                    {/* <button
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      disabled={isSigningUp || isUploadingImage}
                      type="button"
                    >
                      <img src={appleIcon} alt="Apple" className="w-5 h-5" />
                    </button> */}
                  </div>

                  {/* Terms and Conditions */}
                  <div className="auth-terms flex items-center justify-center mt-4 mb-3 sm:mr-10 lg:mr-0">
                    <input
                      type="checkbox"
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                      className="w-4 h-4 accent-[#6D758F] rounded"
                    />
                    <span className="ml-2 text-sm text-[#6D758F]">
                      I have read and agree to the{" "}
                      <a 
                        href="/terms-and-conditions" 
                        className="text-[#6D758F] underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms and Conditions
                      </a>
                    </span>
                  </div>

                </div>
              </div>
            </div>
          </form>
          </div>
        </div>
        {/* Course support and order benefits */}
        <div className="auth-benefits-column flex flex-col justify-start w-full max-w-[520px] mt-10 lg:mt-0 px-4 bg-transparent 1920-br:-translate-x-[80px]">
          {coursesEnabled ? <AuthCourseSupport brand="coursepivot" /> : (
          <div className="auth-benefits-card w-full rounded-[24px] border border-slate-200 bg-white p-6">
            <div className="space-y-2">
              <h2 className="text-[22px] font-semibold text-[#0f172a]">
                Get these premium benefits for free
              </h2>
              <p className="text-sm text-slate-500">
                Everything you need to order with confidence.
              </p>
            </div>

            <div className="auth-benefits-list grid gap-3">
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Turnitin AI report</p>
                  <p className="text-[13px] text-[#475569]">Turnitin report to verify no AI was used, your paper won't be stored in Turnitin's database.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Similarity Report</p>
                  <p className="text-[13px] text-[#475569]">Turnitin report to verify your paper has no plagiarism, paper won't be stored in Turnitin's database.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Unlimited Revisions</p>
                  <p className="text-[13px] text-[#475569]">You can ask for free revisions until you get satisfied.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">On-time Delivery</p>
                  <p className="text-[13px] text-[#475569]">Deadlines are tracked for every order.</p>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;

{/* <LoginInput
  className="px-4"
  id="phoneNumber"
  name="phoneNumber"
  label="Phone Number"
  type="tel"
  value={formData.phoneNumber}
  placeholder="Enter your phone number"
  error={touched.phoneNumber && errors.phoneNumber}
  onChange={(e) =>
    handleInputChange("phoneNumber", e.target.value)
  }
  onBlur={() => handleBlur("phoneNumber", formData.phoneNumber)}
/> */}
